import React from "react";

import { MultiSelect } from "~/components/multi-select";
import { MultiSelectItem } from "~/components/multi-select/item";
import { RadioButtonGroup } from "~/components/radio-button-group";
import { RadioButtonGroupItem } from "~/components/radio-button-group/item";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";
import { ALL_CALCULATIONS, type Calculation } from "~/tools/calculate-value-from-points";
import { useScope } from "~/tools/impulse";
import type { Record } from "~/tools/record";

import {
	WidgetLegend,
	type WidgetLegendMode,
	type WidgetLegendModeVariant,
	type WidgetLegendPosition,
	type WidgetLegendPositionVariant,
	type WidgetLegendTable,
} from "./form-state";

// POSITION

const POSITION_TO_LABEL = {
	bottom: "Bottom",
	right: "Right",
} satisfies Record<WidgetLegendPositionVariant>;

export interface WidgetLegendPositionFieldProps {
	className?: string;
	disabled?: boolean;
	name: string;
	position: WidgetLegendPosition;
}

export const WidgetLegendPositionField: React.FC<WidgetLegendPositionFieldProps> = ({
	className,
	disabled,
	name,
	position,
}) => {
	const scope = useScope();

	return (
		<RadioButtonGroup
			className={className}
			disabled={disabled}
			onValueChange={(value) => position.setInput(value)}
			value={position.getInput(scope)}
		>
			{WidgetLegend.allPositions.map((variant) => (
				<RadioButtonGroupItem key={variant} name={name} value={variant}>
					{POSITION_TO_LABEL[variant]}
				</RadioButtonGroupItem>
			))}
		</RadioButtonGroup>
	);
};

// MODE

const MODE_TO_LABEL = {
	list: "List",
	table: "Table",
} satisfies Record<WidgetLegendModeVariant>;

export interface WidgetLegendModeFieldProps {
	className?: string;
	disabled?: boolean;
	name: string;
	mode: WidgetLegendMode;
}

export const WidgetLegendModeField: React.FC<WidgetLegendModeFieldProps> = ({ className, disabled, name, mode }) => {
	const scope = useScope();

	return (
		<RadioButtonGroup
			className={className}
			disabled={disabled}
			onValueChange={(value) => mode.setInput(value)}
			value={mode.getInput(scope)}
		>
			{WidgetLegend.allModes.map((variant) => (
				<RadioButtonGroupItem key={variant} name={name} value={variant}>
					{MODE_TO_LABEL[variant]}
				</RadioButtonGroupItem>
			))}
		</RadioButtonGroup>
	);
};

// TABLE

const CALCULATION_TO_LABELS = {
	mean: {
		full: "Average value",
		short: "Avg",
	},
	first: {
		full: "First value",
		short: "First",
	},
	"first-number": {
		full: "First numeric value",
		short: "First*",
	},
	last: {
		full: "Last value",
		short: "Last",
	},
	"last-number": {
		full: "Last numeric value",
		short: "Last*",
	},
	sum: {
		full: "Sum of the values",
		short: "Sum",
	},
	min: {
		full: "Minimum value",
		short: "Min",
	},
	max: {
		full: "Maximum value",
		short: "Max",
	},
} satisfies Record<Calculation>;

export interface WidgetLegendTableFieldProps {
	className?: string;
	disabled?: boolean;
	table: WidgetLegendTable;
}

export const WidgetLegendTableField: React.FC<WidgetLegendTableFieldProps> = ({ className, disabled, table }) => {
	const scope = useScope();

	return (
		<MultiSelect
			className={className}
			disabled={disabled}
			onValuesChange={(values) => table.setInput(values)}
			placeholder="Select additional table columns"
			values={table.getInput(scope)}
		>
			{ALL_CALCULATIONS.map((variant) => {
				const { short, full } = CALCULATION_TO_LABELS[variant];

				return (
					<MultiSelectItem key={variant} value={variant}>
						<Tooltip>
							<TooltipTrigger>
								<span>{full}</span>
							</TooltipTrigger>
							<TooltipContent>{short}</TooltipContent>
						</Tooltip>
					</MultiSelectItem>
				);
			})}
		</MultiSelect>
	);
};
